import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { 
  generateAttentionReport, 
  getSavedReports,
  type AttentionReport 
} from '@/services/analytics/attentionReportService';
import { PrintableReport } from './PrintableReport';
import { 
  FileText, 
  Calendar, 
  Users, 
  Clock, 
  TrendingUp, 
  Loader2,
  Download,
  Eye,
  BarChart3,
  MessageSquare,
  HelpCircle,
  Vote,
  Printer
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { nb } from 'date-fns/locale';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--destructive))'];

interface Interview {
  id: string;
  title: string;
  created_at: string;
  interviewee_name: string;
  is_sponsored: boolean;
  sponsor_company: string | null;
}

export function AttentionReportsDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  const [isLoading, setIsLoading] = useState(false);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [selectedInterviews, setSelectedInterviews] = useState<string[]>([]);
  const [reportName, setReportName] = useState('');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [generatedReport, setGeneratedReport] = useState<AttentionReport | null>(null);
  const [savedReports, setSavedReports] = useState<Array<{
    id: string;
    name: string;
    dateRangeStart: string;
    dateRangeEnd: string;
    createdAt: string;
  }>>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch interviews and saved reports
  useEffect(() => {
    const fetchData = async () => {
      const [interviewsResult, reportsResult] = await Promise.all([
        supabase
          .from('interviews')
          .select('id, title, created_at, interviewee_name, is_sponsored, sponsor_company')
          .eq('publication_status', 'published')
          .order('created_at', { ascending: false })
          .limit(100),
        getSavedReports(),
      ]);

      if (interviewsResult.data) {
        setInterviews(interviewsResult.data);
      }
      setSavedReports(reportsResult);
    };

    fetchData();
  }, []);

  const filteredInterviews = interviews.filter(i => 
    i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.interviewee_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleInterview = (id: string) => {
    setSelectedInterviews(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedInterviews.length === filteredInterviews.length) {
      setSelectedInterviews([]);
    } else {
      setSelectedInterviews(filteredInterviews.map(i => i.id));
    }
  };

  const handleGenerateReport = async () => {
    if (selectedInterviews.length === 0) return;

    setIsLoading(true);
    try {
      const report = await generateAttentionReport(
        reportName || `Rapport ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
        selectedInterviews,
        new Date(startDate),
        new Date(endDate)
      );
      
      if (report) {
        setGeneratedReport(report);
        setActiveTab('preview');
        // Refresh saved reports
        const updated = await getSavedReports();
        setSavedReports(updated);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Attention Reports</h1>
          <p className="text-muted-foreground">
            Generer og eksporter oppmerksomhetsrapporter for annonsører
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="generate">Generer rapport</TabsTrigger>
          <TabsTrigger value="preview" disabled={!generatedReport}>Forhåndsvisning</TabsTrigger>
          <TabsTrigger value="saved">Lagrede rapporter ({savedReports.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Report Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Rapportinnstillinger</CardTitle>
                <CardDescription>Velg periode og gi rapporten et navn</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="report-name">Rapportnavn</Label>
                  <Input
                    id="report-name"
                    placeholder="F.eks. Xpeng Kampanje Q1 2026"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Fra dato</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">Til dato</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleGenerateReport} 
                  disabled={isLoading || selectedInterviews.length === 0}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Genererer...
                    </>
                  ) : (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      Generer rapport ({selectedInterviews.length} artikler)
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Article Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Velg artikler</CardTitle>
                <CardDescription>
                  Velg hvilke artikler som skal inkluderes i rapporten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  placeholder="Søk etter artikler..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    {selectedInterviews.length === filteredInterviews.length 
                      ? 'Fjern alle' 
                      : 'Velg alle'}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedInterviews.length} valgt
                  </span>
                </div>
                <ScrollArea className="h-[300px] rounded-md border p-2">
                  <div className="space-y-2">
                    {filteredInterviews.map((interview) => (
                      <div
                        key={interview.id}
                        className="flex items-start space-x-3 rounded-lg p-2 hover:bg-muted/50"
                      >
                        <Checkbox
                          id={interview.id}
                          checked={selectedInterviews.includes(interview.id)}
                          onCheckedChange={() => handleToggleInterview(interview.id)}
                        />
                        <label
                          htmlFor={interview.id}
                          className="flex-1 cursor-pointer text-sm"
                        >
                          <div className="font-medium line-clamp-1">
                            {interview.title}
                            {interview.is_sponsored && (
                              <Badge variant="outline" className="ml-2 text-xs">Betalt Brief</Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {interview.interviewee_name}
                            {interview.sponsor_company && ` • Annonsør: ${interview.sponsor_company}`}
                            {' • '}{format(new Date(interview.created_at), 'dd.MM.yyyy', { locale: nb })}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="print:block">
          {generatedReport && (
            <div>
              <div className="flex justify-end gap-2 mb-4 print:hidden">
                <Button onClick={handlePrint} variant="outline">
                  <Printer className="mr-2 h-4 w-4" />
                  Skriv ut / PDF
                </Button>
              </div>
              <PrintableReport report={generatedReport} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved">
          <Card>
            <CardHeader>
              <CardTitle>Lagrede rapporter</CardTitle>
              <CardDescription>Tidligere genererte rapporter</CardDescription>
            </CardHeader>
            <CardContent>
              {savedReports.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Ingen lagrede rapporter ennå
                </p>
              ) : (
                <div className="space-y-2">
                  {savedReports.map((report) => (
                    <div
                      key={report.id}
                      className="flex items-center justify-between rounded-lg border p-4"
                    >
                      <div>
                        <div className="font-medium">{report.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(report.dateRangeStart), 'dd.MM.yyyy')} - {format(new Date(report.dateRangeEnd), 'dd.MM.yyyy')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(report.createdAt), 'dd.MM.yyyy HH:mm', { locale: nb })}
                        </span>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface ReportPreviewProps {
  report: AttentionReport;
  onPrint: () => void;
}

function ReportPreview({ report, onPrint }: ReportPreviewProps) {
  const engagementData = [
    { name: 'Lesing', value: report.metrics.engagementDistribution.passive, color: 'hsl(var(--primary))' },
    { name: 'Kai Chat', value: report.metrics.engagementDistribution.kai, color: 'hsl(var(--chart-1))' },
    { name: 'Quiz', value: report.metrics.engagementDistribution.quiz, color: 'hsl(var(--chart-2))' },
    { name: 'Debatt', value: report.metrics.engagementDistribution.debate, color: 'hsl(var(--chart-3))' },
    { name: 'Poll', value: report.metrics.engagementDistribution.poll, color: 'hsl(var(--chart-4))' },
  ].filter(d => d.value > 0);

  const heatmapColors = {
    high: 'bg-green-500',
    medium: 'bg-yellow-500',
    low: 'bg-red-500',
  };

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <h2 className="text-2xl font-bold">{report.name}</h2>
        <Button onClick={onPrint}>
          <Download className="mr-2 h-4 w-4" />
          Eksporter PDF
        </Button>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center border-b pb-4">
        <h1 className="text-3xl font-bold text-primary">BRIEFR ATTENTION REPORT</h1>
        <h2 className="text-xl mt-2">{report.name}</h2>
        <p className="text-muted-foreground">
          Periode: {format(new Date(report.dateRangeStart), 'dd.MM.yyyy')} - {format(new Date(report.dateRangeEnd), 'dd.MM.yyyy')}
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-3 print:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{report.metrics.totalReaders.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Lesere</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{Math.round(report.metrics.averageAttentionSeconds)} sek</p>
                <p className="text-sm text-muted-foreground">Gj.snitt tid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{Math.round(report.metrics.averageTAV)}</p>
                <p className="text-sm text-muted-foreground">Gj.snitt TAV</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Sammenligning med industri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="font-medium">Briefr</span>
                <span className="font-bold">{Math.round(report.metrics.averageAttentionSeconds)} sekunder</span>
              </div>
              <Progress value={100} className="h-3" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="font-medium">Facebook (gjennomsnitt)</span>
                <span>1.7 sekunder</span>
              </div>
              <Progress value={(1.7 / report.metrics.averageAttentionSeconds) * 100} className="h-3" />
            </div>
            <div className="text-center pt-4 border-t">
              <Badge variant="secondary" className="text-lg px-4 py-2">
                {Math.round(report.metrics.comparisonMultiplier)}x mer oppmerksomhet enn Facebook
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engagement Distribution & Heatmap */}
      <div className="grid gap-6 md:grid-cols-2 print:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Engasjementsfordeling</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={engagementData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {engagementData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Attention Heatmap</CardTitle>
            <CardDescription>Hvor leserne brukte mest tid</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {report.heatmapData.slice(0, 6).map((zone) => (
                <div key={zone.zoneId} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${heatmapColors[zone.intensity]}`} />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">{zone.label}</span>
                      <span className="text-sm text-muted-foreground">
                        {Math.round(zone.averageSeconds)}s ({zone.percentOfTotal.toFixed(1)}%)
                      </span>
                    </div>
                    <Progress 
                      value={zone.percentOfTotal} 
                      className="h-2 mt-1" 
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kai Insights */}
      {report.kaiInsights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Kai Insights (Zero-Party Data)
            </CardTitle>
            <CardDescription>
              Vanligste spørsmål fra leserne - verdifull innsikt i kunnskapshull
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {report.kaiInsights.slice(0, 5).map((insight, index) => (
                <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">"{insight.queryText}"</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {insight.count} ganger
                      </Badge>
                      {insight.category && (
                        <Badge variant="secondary" className="text-xs">
                          {insight.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total Attention Hours */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-lg opacity-80">Total uforstyrret oppmerksomhet</p>
            <p className="text-5xl font-bold mt-2">
              {report.metrics.totalAttentionHours.toFixed(1)} timer
            </p>
            <p className="text-sm opacity-80 mt-2">
              Dette tilsvarer {Math.round(report.metrics.totalAttentionHours * 60)} minutter med fokusert lesing
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AttentionReportsDashboard;
